/* eslint-disable react/no-unescaped-entities */

import Head from "next/head";

import {
  BookOpen,
  Briefcase,
  Calendar,
  CreditCard,
  Globe,
  Layout,
  MapPin,
  Mic,
  PenLine,
  Phone,
  Share2,
  Star,
} from "lucide-react";

import AppSection from "@/components/marketing/AppSection";
import FoundersSection from "@/components/marketing/FoundersSection";
import HeroSection from "@/components/marketing/HeroSection";
import PartnersSection from "@/components/marketing/PartnersSection";
import Footer from "@/components/marketing/pieces/Footer";
import Navbar from "@/components/marketing/pieces/Navbar";
import PlatformDemoSection from "@/components/marketing/PlatformDemoSection";
import PricingSection from "@/components/marketing/PricingSection";
import RealResultsSection from "@/components/marketing/RealResultsSection";
import ReviewsSection from "@/components/marketing/ReviewsSection";
import { CUSTOMERS } from "@/lib/constants/marketing-items";

import type { HeroCard } from "@/components/marketing/HeroSection";
import type { PlatformTab } from "@/components/marketing/PlatformDemoSection";
import type { CaseStudy } from "@/components/marketing/RealResultsSection";

// ---------------------------------------------------------------------------
// content data
// ---------------------------------------------------------------------------

// TODO: Everything will be moved to Contentful in a followup PR

const HERO_CARDS: HeroCard[] = [
  {
    name: "Ricardo Ruiz",
    company: "1354 Landscape & Sod",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/ricardo-ruiz.webp?updatedAt=1773549313869",
    headline: "From no online presence to $30K projects and 20x more impressions",
    ctaLink: "/case-studies/1354-landscape-and-sod",
  },
  {
    name: "Michael Abraham",
    company: "Superior Exterior",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/michael-abraham.png?updatedAt=1773549314281",
    stat: "3x",
    statLabel: "more calls",
    ctaLink: "/case-studies/superior-exterior",
  },
  {
    name: "Adam Alobaidy",
    company: "Comfort Mechanical",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/adam-alobaidy.png?updatedAt=1773549314512",
    headline: "How Comfort Mechanical increased HVAC leads by 60%",
    ctaLink: "/case-studies/comfort-mechanical",
  },
  {
    name: "Cliff Burwell",
    company: "Blue Mountain Pool Care",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/cliff-burwell.png?updatedAt=1773549314117",
    stat: "+500%",
    statLabel: "increase in leads",
    ctaLink: "/case-studies/blue-mountain-pool-care",
  },
  {
    name: "Todd Porter",
    company: "Apex Junk Removal",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/todd-porter.png?updatedAt=1773549315235",
    headline: "How Apex Junk Removal boosted online leads by 50%",
    ctaLink: "/case-studies/apex-junk-removal",
  },
  {
    name: "Austin Packard",
    company: "Packard Landscape",
    image:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Case%20Studies/austin-packard.webp?updatedAt=1773549313822",
    stat: "2.5",
    statLabel: "months prebooked",
    ctaLink: "/case-studies/packard-landscape",
  },
];

const HERO_CONTENT = {
  badgeSegments: ["AI-Powered", "Built for service pros"],
  heading: (
    <>
      AI-first CRM & Marketing <br className="hidden md:block" />
      for <span className="text-clay-400">Home Service Pros.</span>
    </>
  ),
  description: (
    <>
      From getting found online to winning the call and getting paid —marketing, lead conversion, CRM, scheduling,
      invoicing, and payments. <span className="font-bold text-clay-100">One platform. One AI.</span>
    </>
  ),
  socialProof: {
    rating: "5/5",
    ratingLabel: "on the app store",
    subtext: "No contracts · Cancel anytime",
  },
  trustLine: (
    <>
      Trusted by over <span className="font-bold text-clay-200">1,500</span> service businesses
    </>
  ),
  backgroundVideo:
    "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/HeroBG.webm/ik-video.mp4?updatedAt=1773549203488",
  backgroundImage: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/BGFrame.png",
};

const CASE_STUDIES: CaseStudy[] = [
  {
    company: "Patriot Powerwashing\n& Sanitation",
    shortCompanyName: "Patriot Powerwashing",
    name: "Brian Goody",
    role: "Founder · Phoenix, AZ",
    backgroundImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Brian%20Goody%20Final.00_00_27_22.Still003%20(1).jpg?updatedAt=1773548963605",
    previewVideo:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/BG%2010sec.mp4?updatedAt=1773548964626",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Brian%20Goody%20Final_1.mp4?updatedAt=1773548988313",
    stats: [
      { value: "+$380K", label: "additional revenue, yr 1" },
      { value: "3.4×", label: "lead close rate" },
    ],
    quote:
      "\u201CThey built me a website in 24 hours and that first version was pretty amazing and it was better than anything I had gotten from these companies that I\u2019d spent $15,000 with.\u201D",
  },
  {
    company: "Blue Mountain\nPool Care",
    shortCompanyName: "Blue Mountain Pool Care",
    name: "Cliff Burwell",
    role: "Owner · Blue Mountain, PA",
    backgroundImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/blue-mountain-thumbnail.jpg?updatedAt=1773548963602",
    previewVideo:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Blue%20Mountain%2010sec_1.mp4?updatedAt=1773548964381",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Blue%20Mountain%20Testimonial%20Seq_4.mp4?updatedAt=1773548986851",
    stats: [
      { value: "+500%", label: "increase in leads" },
      { value: "40×", label: "more leads" },
    ],
    quote:
      "\u201CRebolt completely transformed our online presence. We went from barely getting any calls to having more leads than we can handle.\u201D",
  },
  {
    company: "Kelly Holmes.\n— KJC Roofing",
    shortCompanyName: "KJC Roofing",
    name: "Kelly Holmes.",
    role: "Managing Partner · Orange County, CA",
    backgroundImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/KJC%20Final.00_03_00_14.Still001.jpg?updatedAt=1773548963610",
    previewVideo:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/KJC%2010%20sec.mp4?updatedAt=1773548964259",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/KJC%20Final.mp4?updatedAt=1773548990141",
    stats: [
      { value: "2×", label: "revenue growth" },
      { value: "60%", label: "more booked jobs" },
    ],
    quote: "\u201CThe AI follow-up alone has paid for itself ten times over. We never miss a lead now.\u201D",
  },
];

const REAL_RESULTS_CONTENT = {
  badge: "Real Results",
  heading: (
    <>
      <span className="text-white">They were where you are.</span>
      <br />
      <span className="text-clay-400">Then this happened.</span>
    </>
  ),
  storiesLabel: "Success Stories",
};

const PLATFORM_TABS: PlatformTab[] = [
  {
    id: "full-ops",
    label: "Full Operations Suite",
    shortLabel: "Full",
    headline: "7 subscriptions in one.",
    description: "Turn completed jobs into your best salesperson. Marketing & CRM working together.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/dashboard.png?updatedAt=1773548951965",
    leftPhones: [
      {
        src: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/reviews.png?updatedAt=1773548952013",
        alt: "Reviews",
      },
    ],
    rightPhones: [
      {
        src: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/quotes.png?updatedAt=1773548952015",
        alt: "Quotes",
      },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    shortLabel: "CRM",
    headline: "Estimate. Schedule. Get Paid.",
    description: "Capture leads, manage communications, send quotes, close jobs, invoice clients, and collect reviews.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/invoices.png?updatedAt=1773548951959",
    // ctaLink: "/features/home-service-quoting-tool",
    leftCards: [
      { icon: Calendar, title: "Appointments", description: "Track active jobs and progress from start to finish." },
      {
        icon: BookOpen,
        title: "Quotes & Pricebook",
        description: "Create and send professional estimates within minutes.",
      },
      { icon: CreditCard, title: "Invoicing & Payments", description: "Send invoices and collect payments with ease." },
    ],
    rightCards: [
      { icon: Briefcase, title: "Jobs", description: "Track active jobs and progress from start to finish." },
      {
        icon: Globe,
        title: "Client Management",
        description: "Organize contacts, notes, and communication in one place.",
      },
      { icon: Share2, title: "Client Portal", description: "Share job details, estimates, and updates with clients." },
    ],
  },

  {
    id: "marketing-kit",
    label: "Marketing Kit",
    shortLabel: "Marketing",
    headline: "Online presence, boosted by AI.",
    description: "Website, social media, and review management, continuously optimized by AI.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/review-response.png?updatedAt=1773548951961",
    // ctaLink: "/features/marketing-kit",
    leftCards: [
      {
        icon: Layout,
        title: "AI Website Builder",
        description: "Generate, customize, and publish SEO-optimized sites in 24 hours.",
      },
      { icon: Share2, title: "Social Media Manager", description: "Post, geotag across platforms (Desktop & Mobile)." },
      { icon: MapPin, title: "Google Business Profile Optimizer", description: "Automated GBP management." },
    ],
    rightCards: [
      { icon: Star, title: "Review Management", description: "Automate the review loop, replace standalone tools." },
      { icon: MapPin, title: "Projects Geotagging", description: "Showcase completed work with location data." },
      {
        icon: PenLine,
        title: "Auto-Blogging",
        description: "Automated blog post suggestions drafted by AI based on your data.",
      },
    ],
  },
  {
    id: "voice-agent",
    label: "Voice Agent",
    shortLabel: "Voice Agent",
    headline: "Never miss another call.",
    description:
      "Your AI voice agent answers every inbound call, qualifies leads, books appointments, and follows up — so you never miss a job.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/call-detail.png?updatedAt=1773548951966",
    // ctaLink: "/features/voice-agent",
    leftCards: [
      {
        icon: Mic,
        title: "AI Voice Assistant",
        description:
          "It answers your phone like you. Qualifies the lead. Books the job. Logs everything. Then sends it all to your CRM automatically.",
      },
    ],
    rightCards: [
      {
        icon: Phone,
        title: "Call Sync",
        description: "Every call is recorded, transcribed, and tagged automatically. Pull it up anytime, anywhere.",
      },
    ],
  },
];

const PLATFORM_CONTENT = {
  badge: "The Platform",
  heading: (
    <>
      One platform. All the tools
      <br className="hidden md:block" /> you need to <span className="text-[#c4aa88]">win.</span>
    </>
  ),
  ctaLabel: "Learn More",
};

const APP_CONTENT = {
  badge: "Operate on the go",
  heading: "Take the Rebolt App wherever the job takes you.",
  description:
    "Run your entire business in your phone — capture leads, manage conversations, send quotes, close jobs, invoice clients, and collect reviews, all from your phone.",
  ctaLabel: "Book your demo",
  backgroundImage: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/app-section-bg.png",
};

const CORE_VALUES = [
  {
    title: "Growth without clarity is chaos",
    description:
      "Marketing tools shouldn\u2019t create more noise \u2014 they should create momentum. Too many platforms add dashboards, features, and complexity, but don\u2019t help you turn interest into revenue. Rebolt exists to connect the dots between marketing and customer relationships, so every message, campaign, and follow-up moves the needle.",
  },
  {
    title: "Your customer data is your unfair advantage.",
    description:
      "You shouldn\u2019t rent your audience. We believe businesses should own their contacts, conversations, and insights \u2014 not lose them across disconnected tools. Rebolt brings your marketing communications and CRM into one system, so you build real relationships, not just campaigns.",
  },
  {
    title: "Automation should feel human.",
    description:
      "Great marketing doesn\u2019t feel automated \u2014 it feels timely. We design Rebolt so automation enhances connection instead of replacing it. The right message, to the right person, at the right moment \u2014 without losing the personal touch that builds trust.",
  },
];

const FOUNDERS_CONTENT = {
  badge: "OUR CORE VALUES",
  heading: "We stand for simplicity at scale.",
  foundersImage: "/cofounders.png",
  foundersLabel: "Our founders",
  founders: ["Will", "Arwin"],
};

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

const BASE_URL = process.env.NEXT_PUBLIC_API_HOST ?? "https://rebolthq.com";

export default function HomeLanding() {
  return (
    <>
      <Head>
        <title>AI-Powered CRM & Marketing for Home Service Pros | Rebolt</title>
        <meta
          name="description"
          content="Rebolt replaces 10 tools with one AI-powered platform for home service pros. Capture more leads, book more jobs, and operate your business — all at one fair price."
        />
        <meta property="og:image" content={`${BASE_URL}/marketing-pages/OGImage.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${BASE_URL}/marketing-pages/OGImage.png`} />
      </Head>

      <Navbar />

      <main className="overflow-hidden bg-clay-100 relative">
        <HeroSection cards={HERO_CARDS} content={HERO_CONTENT} />

        <RealResultsSection caseStudies={CASE_STUDIES} content={REAL_RESULTS_CONTENT} />

        <div className="flex flex-col items-center justify-center relative">
          <div className="pt-8 md:pt-5 md:pb-16 bg-ash-900 w-full">
            <PartnersSection label="Trusted by" logos={CUSTOMERS} />
          </div>

          <PlatformDemoSection tabs={PLATFORM_TABS} content={PLATFORM_CONTENT} />

          <PricingSection />

          <ReviewsSection />

          <AppSection content={APP_CONTENT} />

          <FoundersSection values={CORE_VALUES} content={FOUNDERS_CONTENT} />

          <Footer />
        </div>
      </main>
    </>
  );
}
